import { signal } from "./signal";
import { create, fromBinary, toBinary, type MessageInitShape } from "@bufbuild/protobuf";
import {
  EnvelopeSchema,
  type Envelope,
  type CbTableState,
  type MatchStatus,
  type RoomState,
  type TableState,
  type Suit,
  type Card,
  type TableEvent,
  type HkTableState,
  type HkCard,
  type WalletState,
  type PublicTable,
  type ChatMessage,
  type SubscriptionState, type TodayState, type HkEvent , type CbEvent,
  type Notice,
  type ShTableState, type ShEvent,
  type BoardTableState,
  type CupUpdate,
  type MafiaState,
  MafiaActionSchema,
} from "../gen/bazivan/v1/table_pb";
import { deviceId, httpUrl, renewDeviceId, serverBase, wsUrl } from "./config";
import { pendingSource } from "./source";
import { Pace } from "./pace";
import { avatar } from "../game/avatar";
import { menchPiece, saveMenchPiece } from "../game/menchPiece";

/**
 * signedOut: این مرورگر حسابی ندارد و مهمانی هم برایش ساخته نشده. صفحه‌ی بازی تا وقتی کاری سرور
 * را نخواهد (نشستن سرِ میز، تماشا) مهمان نمی‌سازد — [[Client.seat]].
 */
export type ConnState = "offline" | "loggingIn" | "connecting" | "online" | "reconnecting" | "signedOut";

/** نسخه‌ی پروتکل — ۲: «ادامه» ِ میانِ دو دست (HandReady/HandBreak)؛ Backend/internal/ws/hub.go. */
const PROTOCOL = 2;

/** چند پیامِ چت در حافظه می‌مانَد — بیش از این، فهرستِ صفحه بی‌دلیل سنگین می‌شود. */
const CHAT_KEEP = 120;

/**
 * اتصالِ کلاینتِ وب به bazivand: ورودِ مهمان (REST) → WebSocket → Hello/Welcome.
 *
 * همان قراردادِ نسخه‌ی موبایل، با همان .proto. سه چیزی که آن‌جا به آن رسیدیم
 * این‌جا هم هست، چون درسِ گران‌قیمتی بودند:
 *   • صندوقِ خروجی — لمسِ بازیکن پیش از برقراریِ اتصال نباید دور ریخته شود.
 *   • بازاتصالِ خودکار با همان توکن؛ سرور تا مهلتِ بازگشت، میز را نگه می‌دارد.
 *   • آخرین وضعیتِ میز نگه داشته می‌شود تا صفحه‌ی میز، که یک فریم دیرتر ساخته
 *     می‌شود، میزِ خالی نبیند.
 */
/** یکی از «میزهای من» — آینه‌ی store.PlayerTable ِ سرور. table: شهر و فرش (game/carpet.ts tableKey)، back: پشتِ کارتِ میز. */
export type MyTableRow = { id: string; name: string; table: string; back: string; created?: string };

export class Client {
  /**
   * نشانی و شناسه را می‌شود صریح داد.
   *
   * برای آزمونِ زنده لازم است: دو کلاینت با دو شناسه‌ی متفاوت باید هم‌زمان زنده
   * باشند، وگرنه سرور هر دو را یک بازیکن می‌بیند و اتصالِ اولی را می‌بندد —
   * همان تله‌ای که در آزمونِ دو‌نفره‌ی نسخه‌ی موبایل خوردیم.
   */
  constructor(
    private readonly base: string = serverBase,
    private device: string = deviceId,
    /** شناسه‌ی تازه پس از «device_taken» — [[renewDeviceId]]. */
    private readonly renew: () => string = renewDeviceId,
  ) {}

  readonly state = signal<ConnState>("offline");
  /** مهمانِ تازه ساخته شود؟ فقط بعد از connect({create: true}) — [[connect]]. */
  private createGuest = false;
  /**
   * سرور این شناسه را نمی‌شناسد (۴۰۴ ِ create: false، شناسه‌ی تازه پس از device_taken، خروج). فقط آن وقت مهمانِ
   * تازه نامِ موقت می‌گیرد ([[tempGuestName]]) — نامِ دستگاهِ شناخته‌شده با ورودِ دوباره عوض نمی‌شود.
   */
  private freshDevice = false;
  readonly lastError = signal("");
  /**
   * «کمی آرام‌تر»: لمسی که سرعت‌گیر کنار گذاشت (net/pace.ts) — App یک پیامِ گذرا نشان می‌دهد. نه
   * lastError، که روی نوارِ اتصال می‌ماند و جای خطای واقعی است. هر چند ثانیه حداکثر یک بار.
   */
  readonly slowed = signal(0);

  // ---- سرعت‌گیرها — net/pace.ts؛ اعداد همان Backend/internal/ws/pace.go ----
  /** هر پیامِ چت، متن یا عبارتِ آماده؛ همان پیام دوباره در ۴ ثانیه نمی‌رود. */
  readonly chatPace = new Pace(1200, 3, 4000);
  /** عبارتِ آماده و واکنشِ یک‌لمسیِ تخته‌ای — یک لمس است و برای همه حباب و صدا دارد. */
  readonly cannedPace = new Pace(2000, 3);
  /** کلیدهای میزبان: عمومی، چت، فرش — هر کدام وضعیتِ اتاق را برای همه می‌فرستد. */
  readonly hostPace = new Pace(1000, 4);
  /** ساختن، پیوستن، تماشا، شروع: لمسِ دوباره‌ی همان دکمه در یک ثانیه نمی‌رود. */
  readonly roomPace = new Pace(1000, 5, 1000);
  /**
   * کدِ اتاقی که سرور برایش رمز خواست — نما همین را می‌بیند و رمز می‌پرسد.
   *
   * چرا سیگنالِ جدا و نه همان lastError: پیامِ خطا یک جمله است و صفحه از
   * رویش نمی‌فهمد باید فیلدِ رمز باز کند یا نه. کد هم لازم است تا بشود
   * همان اتاق را دوباره امتحان کرد.
   */
  readonly needRoomPassword = signal<string | null>(null);
  private lastRoomCode = "";
  /**
   * این صفحه کنار گذاشته شد چون همان حساب جای دیگری وصل شد.
   *
   * جدا از `state` است چون درمانش فرق می‌کند: قطعیِ عادی خودش برمی‌گردد، این
   * یکی برنمی‌گردد و **نباید** هم خودبه‌خود برگردد — دو زبانه بی‌پایان
   * همدیگر را بیرون می‌انداختند. تنها راهش این است که آدم بگوید کدام صفحه
   * برنده باشد، و برای آن باید اول بفهمد چه شده.
   */
  readonly kicked = signal(false);
  /** نسخه‌ی پروتکلِ سرور از Welcome؛ ۰ تا وصل‌شدن. ۲ به بعد «ادامه» ِ میانِ دو دست دارد. */
  serverProtocol = 0;
  readonly playerId = signal("");
  readonly playerName = signal("");
  /** نامِ کاربریِ ثبت‌شده؛ خالی یعنی هنوز مهمان است. */
  readonly username = signal("");
  /** ایمیل تأیید شده؟ حساب تا آن‌وقت «فعال» نیست و سرِ میزِ آنلاین نمی‌نشیند. */
  readonly verified = signal(false);
  /** ایمیلِ حساب؛ خالی برای مهمان. */
  readonly email = signal("");
  /**
   * کشورِ خودم (ISO2) — سرور از IP تشخیص می‌دهد و ایرانیِ پشتِ VPN را ۳۰ روز
   * ایرانی نگه می‌دارد. شهر اختیاری و فقط برای ایران. showPlace: کشور و شهرم
   * به بقیه نشان داده شود (پیش‌فرض بله؛ در تنظیمات خاموش می‌شود).
   */
  readonly country = signal("");
  readonly city = signal("");
  readonly showPlace = signal(true);

  /**
   * کیفِ پول — مرجعش سرور است.
   *
   * سرور بی‌درخواست می‌فرستد: یک بار بعدِ Welcome و بعد از هر تغییر (جایزه‌ی
   * میز، ورودی، خرید). کلاینت هرگز خودش کم و زیاد نمی‌کند؛ حتی یک «۱۸۰+»ِ
   * خوش‌بینانه یعنی عددی که سرِ خرید غیب می‌شود.
   *
   * null یعنی هنوز چیزی نرسیده — با «صفر» یکی نیست و UI باید فرقشان را بگذارد.
   */
  readonly wallet = signal<WalletState | null>(null);

  /** آخرین تغییرِ کیف، برای انیمیشنِ «۱۸۰+». */
  readonly walletDelta = signal<WalletState | null>(null);

  /** انبار: شناسه‌ی قلم → تعداد. */
  readonly inventory = signal<ReadonlyMap<string, number>>(new Map());

  /**
   * پشتِ کارتِ به‌کاربسته‌ی حساب؛ خالی = پیش‌فرض. مرجعش سرور است — پاسخِ ورود
   * و [[setBack]] — تا گوشی و مرورگر یک پشت ببینند. میز با همین می‌کشد
   * (game/cardBack.ts).
   */
  readonly back = signal("");

  /**
   * «میزهای من» — میزهایی که مشتری در فروشگاه ساخته (شهر، فرش، پشتِ کارت). null = هنوز نخوانده
   * ([[loadMyTables]]). سرِ ساختنِ اتاق یکی با "table:<شناسه>" فرستاده می‌شود و سرور از همین فهرست
   * (فهرستِ خودِ میزبان) برش می‌دارد.
   */
  readonly myTables = signal<MyTableRow[] | null>(null);

  /**
   * اشتراکِ ماهانه. مرجعش سرور است: انقضا آن‌جا سنجیده می‌شود و ساعتِ
   * مرورگر — که کاربر می‌تواند عوضش کند — در تصمیم دخالتی ندارد.
   */
  readonly subscription = signal<SubscriptionState | null>(null);

  /**
   * «امروز»: بازیِ روز و مأموریت‌ها. null = هنوز نپرسیده‌ایم.
   *
   * درخواستی است و نه بی‌درخواست: سرور سرِ Hello نمی‌فرستدش، چون کسی که
   * این صفحه را باز نمی‌کند نباید هزینه‌اش را بدهد.
   */
  readonly today = signal<TodayState | null>(null);

  /**
   * میزهای عمومیِ زنده — پاسخِ ListTables.
   *
   * سرور خودش نمی‌فرستد و صفحه‌ی تماشا هر چند ثانیه می‌پرسد: فهرست تا وقتی
   * کسی بازش نکرده بی‌مصرف است، و پخشِ خودکارش یعنی هر میزِ تازه به همه‌ی
   * اتصال‌های بی‌کارِ سرور هم می‌رسد.
   */
  readonly tables = signal<readonly PublicTable[]>([]);

  /** شناسه‌ی میزی که دارم تماشا می‌کنم؛ خالی = تماشایی در کار نیست. */
  readonly watching = signal("");

  /**
   * پیام‌های چتِ میزِ جاری — تازه‌ترین در ته.
   *
   * با هر میز از نو شروع می‌شود: چتِ یک میز مالِ همان میز است و جایی ذخیره
   * نمی‌شود، نه این‌جا و نه در سرور.
   */
  readonly chat = signal<readonly ChatMessage[]>([]);

  readonly room = signal<RoomState | null>(null);
  readonly match = signal<MatchStatus | null>(null);
  readonly table = signal<TableState | null>(null);
  readonly cbTable = signal<CbTableState | null>(null);
  readonly shTable = signal<ShTableState | null>(null);
  /**
   * میزِ تخته‌ای (منچ، دوز، …) — یک نما برای همه‌شان؛ بازی را خودِ نما می‌گوید (`game`).
   * رمزِ هر بازی در Backend/internal/boards/<بازی> و src/board/state.ts است.
   */
  readonly board = signal<BoardTableState | null>(null);
  /**
   * میزِ مافیا — نمای همین بازیکن (نقشِ دیگران فقط اگر حق دارد بداند). جریانِ رویدادها و یادداشت‌ها
   * با MafiaEvent جدا می‌آید و src/mafia/controller.ts آن را نگه می‌دارد.
   */
  readonly mafia = signal<MafiaState | null>(null);
  /**
   * سرور برای تعمیر بسته است — پیامش، یا رشته‌ی خالی وقتی باز است.
   *
   * جدا از lastError نگه داشته می‌شود چون این یکی *حالت* است و نه یک رویدادِ
   * گذرا: تا وقتی بسته است باید روی صفحه بمانَد، و toastِ یک‌بارمصرف همان
   * چیزی بود که بازیکن نمی‌دیدش.
   */
  readonly maintenance = signal<string>("");
  readonly hkTable = signal<HkTableState | null>(null);
  /**
   * آس‌کشیِ آغازِ مسابقه.
   *
   * سیگنال است نه رویداد، چون سرور آن را **پیش از** اولین وضعیت می‌فرستد —
   * یعنی پیش از آن‌که میز اصلاً روی صفحه سوار شده باشد. با شنونده‌ی درونِ
   * کامپوننت، رویداد همیشه از دست می‌رفت (و می‌رفت: مراسم هیچ‌وقت دیده نشد).
   * این‌جا می‌مانَد تا میز سوار شود و بخوانَدش، و بعد خودش پاکش می‌کند.
   */
  readonly aceDraw = signal<TableEvent | null>(null);

  /**
   * پخشِ دستِ تازه‌ی هفت‌خبیث — به همان دلیلِ بالا سیگنال است نه رویداد.
   *
   * سرور «deal» را **پیش از** اولین وضعیت می‌فرستد، یعنی پیش از آن‌که میز
   * اصلاً روی صفحه سوار شده باشد؛ با شنونده‌ی درونِ کامپوننت همیشه از دست
   * می‌رفت و کارت‌ها یک‌باره پخش‌شده روی صفحه بودند.
   */
  readonly hkDeal = signal<HkEvent | null>(null);

  /** پخشِ دستِ تازه‌ی چهاربرگ — به همان دلیلِ [[hkDeal]] سیگنال است نه رویداد. */
  readonly cbDeal = signal<CbEvent | null>(null);
  /** آس‌کشیِ چهاربرگ و هفت — به همان دلیلِ [[aceDraw]] سیگنال‌اند: مراسم پیش
   *  از سوارشدنِ صحنه می‌رسد و رویدادِ گذرا گم می‌شد. */
  readonly cbAce = signal<CbEvent | null>(null);
  /** پخشِ شلم و مراسمِ آس‌کشی‌اش — مثلِ چهاربرگ، برای صحنه‌ای که دیر سوار می‌شود. */
  readonly shDeal = signal<ShEvent | null>(null);
  readonly shAce = signal<ShEvent | null>(null);
  /** زمینِ حاکم: چهار برگ که فقط به خودش می‌رسد و صحنه باید نشانشان بدهد. */
  readonly shWidow = signal<ShEvent | null>(null);
  readonly hkAce = signal<HkEvent | null>(null);

  /**
   * اعلان‌های سرور (Notice): پیامِ همگانیِ ادمین، پاسخِ پشتیبانی، اخطار،
   * تعمیرات. تازه‌ترین در ته؛ در همین نشست می‌مانند و صندوق (GET /v1/inbox)
   * تاریخچه‌ی ماندگار را دارد. `lastNotice` برای پیامِ گذرای روی صفحه است.
   */
  readonly notices = signal<readonly Notice[]>([]);
  readonly lastNotice = signal<Notice | null>(null);

  /**
   * جامِ آرنایی که بازیکن در صفش ایستاده یا سرِ میزش نشسته — docs/friday-cup.md.
   *
   * «بازیِ بعدی» ِ پایانِ میز از همین می‌فهمد به کدام صف برگردد: تا جام باز است
   * به صفِ جام، نه به صفِ عادی. با بازیِ سریعِ عادی، میزِ رباتی یا اتاق پاک
   * می‌شود ([[quickMatch]])، و با گذشتنِ پایانِ جام بی‌اثر ([[liveCup]]).
   */
  readonly cup = signal<CupRef | null>(null);
  /**
   * نتیجه‌ی آخرین میزِ جام — سرور پس از هر میزِ جام بی‌درخواست می‌فرستد.
   *
   * سیگنال است نه رویداد، به همان دلیلِ [[aceDraw]]: ممکن است پیش از سوارشدنِ
   * پرده‌ی پایانِ مسابقه برسد، یا بعد از آن‌که بازیکن از میز بیرون رفته.
   */
  readonly cupUpdate = signal<CupUpdate | null>(null);

  private ws: WebSocket | null = null;
  private token = "";
  private seq = 0;
  /**
   * بالاترین `v`ی که تا حالا اعمال شده، و میزی که مالِ آن است.
   *
   * قانون یک خط است: هرچه `v`ِ کوچک‌تر یا مساوی داشته باشد، کهنه است و دور
   * می‌رود. با میزِ تازه صفر می‌شود، چون شمارنده مالِ هر میز است و میزِ تازه
   * از یک شروع می‌کند — بی این بازنشانی، اولین میزِ دومِ هر نشست کاملاً
   * نادیده می‌رفت و صفحه خالی می‌مانْد.
   */
  private lastV = 0n;
  private vTable = "";
  private outbox: Envelope[] = [];
  private want = false;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private backoff = 500;
  private listeners = new Set<(e: Envelope) => void>();

  /**
   * درخواستِ REST با توکنِ همین بازیکن — برای صندوق و پشتیبانی که روی سوکت
   * نیستند. خروجی JSON یا null (۲۰۴)؛ خطا با کدِ سرور پرتاب می‌شود.
   */
  async rest<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (this.token) headers.authorization = "Bearer " + this.token;
    if (body !== undefined) headers["content-type"] = "application/json";
    let res: Response;
    try {
      res = await fetch(httpUrl(path), { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    } catch { throw new Error("network"); }
    if (res.status === 204) return null as T;
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(String((j as { error?: string }).error ?? "http_" + res.status));
    return j as T;
  }

  /** هر پیامِ رسیده. برای رویدادهای لحظه‌ای (انیمیشن) که در وضعیت نمی‌گنجند. */
  on(fn: (e: Envelope) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * وصل‌شدن. مهمان فقط با `create: true` ساخته می‌شود — وقتی کاری سرور را می‌خواهد (نشستن سرِ میز،
   * تماشا). پیش‌فرض فقط می‌پرسد: این مرورگر حسابی دارد؟ اگر نه، signedOut و بی سطرِ تازه در پایگاه.
   *
   * تا پیش از این هر بازکردنِ /play در مرورگرِ تازه یک مهمانِ بی‌نام می‌ساخت، هرچند بیشترشان هیچ‌وقت
   * سرِ میز نمی‌نشستند (۱۷ از ۲۱ مهمانِ سرورِ اصلی همین بودند). حساب هم با ورود
   * (/v1/account/login، بی مهمانِ پیشین) یا ثبت‌نام در /account.html به این دستگاه می‌چسبد.
   */
  async connect(opts: { create?: boolean } = {}): Promise<void> {
    this.want = true;
    this.kicked.value = false;
    if (opts.create) this.createGuest = true;
    if (this.state.value === "signedOut" && !this.createGuest) return;
    if (this.state.value !== "offline" && this.state.value !== "reconnecting" && this.state.value !== "signedOut") return;
    if (!this.token) {
      this.state.value = "loggingIn";
      try {
        const ask = () => fetch(this.base + "/v1/auth/guest", {
          method: "POST",
          headers: { "content-type": "application/json" },
          // کوکیِ کلیدِ دستگاه (HttpOnly) با همین درخواست می‌رود — config.ts/renewDeviceId.
          credentials: "same-origin",
          // نامِ محلیِ نسلِ قبل فقط برای **ساختنِ** حسابِ تازه به درد می‌خورد؛
          // سرور نامِ حسابِ موجود را با این عوض نمی‌کند — [[adoptLocalName]].
          // src: لینکِ ردیابی‌ای که این مرورگر با آن آمد. سرور فقط برای دستگاهِ تازه
          // نگهش می‌دارد، پس فرستادنش سرِ ورودِ دوباره بی‌اثر است — net/source.ts.
          body: JSON.stringify({ device_id: this.device, name: savedName() || (this.freshDevice ? tempGuestName(this.device) : ""),
            create: this.createGuest, src: pendingSource() }),
        });
        let res = await ask();
        if (res.status === 409 && (await errorOf(res)) === "device_taken") {
          // شناسه بی کلیدش به کار نمی‌آید: یک بار با شناسه‌ی تازه. حسابِ ثبت‌شده‌ای که روی
          // شناسه‌ی قبلی بود با نام و رمز برمی‌گردد.
          this.device = this.renew();
          this.freshDevice = true;
          res = await ask();
        }
        if (res.status === 404) this.freshDevice = true;
        if (res.status === 404 && !this.createGuest) {
          // دستگاهِ ناشناس و نساختن: بی خطا، بی تلاشِ دوباره — صفحه دکمه‌های بازی را نشان می‌دهد و
          // اولین «بازی» مهمان را می‌سازد ([[seat]]).
          this.lastError.value = "";
          this.state.value = "signedOut";
          return;
        }
        // بازیکن همان وقتی «بازی» زد که صفحه هنوز می‌پرسید (create: false): همین حالا با ساختن.
        if (res.status === 404) res = await ask();
        if (!res.ok) throw new Error("ورود ناموفق (" + res.status + ")");
        const j = (await res.json()) as AuthResp;
        this.freshDevice = false;
        this.token = j.token;
        this.playerId.value = j.player_id;
        this.playerName.value = j.name ?? "";
        this.username.value = j.username ?? "";
        this.verified.value = !!j.verified;
        this.email.value = j.email ?? "";
        this.country.value = j.country ?? "";
        this.city.value = j.city ?? "";
        this.showPlace.value = j.show_place !== false;
        this.back.value = j.back ?? "";
      } catch (e) {
        this.fail("ورود: " + msgOf(e));
        return;
      }
      await this.adoptLocalName();
    }
    this.open();
  }

  /**
   * نامِ محلیِ نسلِ قبل: یک بار به سرور پیشنهاد می‌شود و کلید پاک می‌شود.
   *
   * تا دیروز نام فقط در localStorage بود و سرِ هر ورود فرستاده می‌شد. حالا
   * منبعِ حقیقت سرور است، پس این کلید باید برود — ولی نه پیش از آن‌که نامش
   * به جای امن برسد. اگر سرور از قبل نامی دارد، همان می‌مانَد: نامی که روی
   * گوشیِ دیگری گذاشته شده تازه‌تر از نامی است که در این مرورگر مانده.
   */
  private async adoptLocalName(): Promise<void> {
    const local = savedName();
    if (!local) return;
    if (!this.playerName.value) await this.setName(local);
    try { localStorage.removeItem("bazivan.name"); } catch { /* حالتِ ناشناس */ }
  }

  /**
   * خروج از حسابِ ثبت‌شده. این مرورگر «خارج» می‌ماند (signedOut) و مهمانِ تازه‌ای برایش
   * ساخته نمی‌شود (create: false) — پیش از این هر خروج یک مهمانِ بی‌استفاده می‌ساخت. حساب
   * با نام و رمز در /account.html برمی‌گردد. خروجی: null = موفق، وگرنه not_registered / network …
   */
  async logout(): Promise<string | null> {
    if (!this.token) return "network";
    try {
      const res = await fetch(this.base + "/v1/account/logout", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: "Bearer " + this.token },
        body: JSON.stringify({ device_id: this.device, create: false }),
      });
      if (!res.ok) {
        try { return ((await res.json()) as { error?: string }).error ?? "http_" + res.status; } catch { return "http_" + res.status; }
      }
      const j = (await res.json()) as AuthResp & { signed_out?: boolean };
      if (j.signed_out) {
        this.token = "";
        this.createGuest = false;
        this.freshDevice = true;           // دستگاه از حساب جدا شد؛ مهمانِ بعدی تازه است
        this.playerId.value = "";
        this.playerName.value = "";
        this.username.value = "";
        this.verified.value = false;
        this.email.value = "";
        this.country.value = "";
        this.city.value = "";
        this.showPlace.value = true;
        this.back.value = "";
        this.myTables.value = null;
        this.wallet.value = null;
        this.inventory.value = new Map();
        this.want = false;               // سوکت بسته می‌ماند؛ بازگشتِ خودکار مهمان نمی‌سازد
        this.ws?.close();
        this.state.value = "signedOut";
        return null;
      }
      if (!j.token) return "network";
      this.token = j.token;
      this.playerId.value = j.player_id;
      this.playerName.value = j.name ?? "";
      this.username.value = "";
      this.verified.value = false;
      this.email.value = "";
      this.country.value = j.country ?? "";
      this.city.value = j.city ?? "";
      this.showPlace.value = j.show_place !== false;
      this.back.value = j.back ?? "";
      this.myTables.value = null;        // «میزهای من» ِ حسابِ قبلی؛ مالِ این حساب با loadMyTables می‌آید
      this.wallet.value = null;          // کیفِ حسابِ قبلی دیگر مالِ ما نیست
      this.inventory.value = new Map();
      this.ws?.close();                  // بازگشتِ خودکار با توکنِ مهمان Hello می‌فرستد
      return null;
    } catch {
      return "network";
    }
  }

  /**
   * «بی حساب بنشین» ِ لینکِ اتاق: مهمانِ همین مرورگر با این نام، همین حالا. مرورگرِ بی‌حساب مهمانش را می‌سازد
   * (create: true) و نام همان دم با ساختن می‌رود (savedName) تا میزبان «سارا» ببیند، نه «مهمان ۱۲۳۴»؛ مهمانِ
   * موجود اگر نامِ تازه زده باشد، نامش عوض می‌شود. نامِ خالی = نامِ موقتِ سرور. پیوستن به اتاق کارِ App است.
   */
  async seatAsGuest(name: string): Promise<void> {
    const n = name.trim();
    if (this.state.value === "online") {
      if (n && n !== this.playerName.value) await this.setName(n);
      return;
    }
    if (n) { try { localStorage.setItem(NAME_KEY, n); } catch { /* بی‌ذخیره: نامِ موقت */ } }
    await this.connect({ create: true });
  }

  /**
   * نامِ نمایشی. منبعِ حقیقت سرور است و نه این مرورگر.
   *
   * جوابِ سرور **توکنِ تازه** دارد، چون نام در ادعاهای توکن است و سوکت از
   * همان‌جا می‌خوانَدش؛ بی توکنِ تازه، نامِ تازه سرِ میز نمی‌آمد. پس سوکت هم
   * بسته می‌شود تا Helloِ بعدی نامِ تازه را ببرد.
   *
   * خروجی: `{error: null}` = شد. `too_soon` مهلتش را هم می‌آورد — «نمی‌شود»
   * بی «کِی می‌شود» بازیکن را هر روز برمی‌گردانَد.
   */
  async setName(name: string): Promise<{ error: string | null; until?: string }> {
    // بی‌حساب و هنوز بی مهمان: نام همین‌جا می‌مانَد و با اولین «بازی» همراهِ ساختنِ مهمان می‌رود
    // ([[seat]]، savedName) — مهمان فقط برای عوض‌کردنِ نام ساخته نمی‌شود.
    if (!this.token && this.state.value === "signedOut") {
      try { localStorage.setItem(NAME_KEY, name); } catch { return { error: "network" }; }
      this.playerName.value = name;
      return { error: null };
    }
    const j = await this.accountPost<{ token?: string; name?: string; until?: string }>("/v1/account/name", { name });
    if (j.error) return { error: j.error, until: j.until };
    if (j.token) this.token = j.token;
    this.playerName.value = j.name ?? name;
    this.ws?.close();
    return { error: null };
  }

  /**
   * ایمیلِ تازه: کد به **همان نشانیِ تازه** می‌رود و تا تأیید هیچ‌چیزی عوض
   * نمی‌شود. رمزِ فعلی لازم است. خروجی: null = کد رفت.
   */
  async changeEmail(email: string, password: string): Promise<string | null> {
    return (await this.accountPost("/v1/account/email", { email, password })).error;
  }

  /** کدِ ایمیلِ تازه. خروجی: خطا، یا null و ایمیلِ تازه در client.email. */
  async verifyEmail(code: string): Promise<string | null> {
    const j = await this.accountPost<{ email?: string }>("/v1/account/email/verify", { code });
    if (j.error) return j.error;
    this.email.value = j.email ?? "";
    this.verified.value = true;
    return null;
  }

  /**
   * خریدِ قلمِ فروشگاه. خروجی: null = خرید شد، وگرنه کدِ خطای سرور.
   *
   * بها فرستاده نمی‌شود — سرور از کاتالوگِ خودش برمی‌دارد. `ref` کلیدِ
   * یکتاییِ تلاش است تا تکرارِ شبکه دو بار نخرد.
   *
   * جوابِ موفق **seq ندارد** (سرور انبارِ تازه را بی‌درخواست می‌فرستد)، پس
   * موفقیت از رسیدنِ انبار فهمیده می‌شود و شکست از خطای هم‌شماره.
   */
  /**
   * `currency`: جنسِ بهایی که صفحه نشان داد — از کاتالوگ. سرور اگر با جنسِ
   * قلم نخوانَد، update_required می‌دهد و چیزی برنمی‌دارد.
   */
  buyItem(item: string, currency: "coins" | "gems" = "coins"): Promise<string | null> {
    const ref = newRef();
    const seq = this.seq + 1;
    if (!this.send({ case: "buyItem", value: { item, ref, currency } })) return Promise.resolve("offline");
    return new Promise((resolve) => {
      const done = (err: string | null) => { off(); clearTimeout(timer); resolve(err); };
      const off = this.on((env) => {
        if (env.msg.case === "error" && env.seq === seq) done(env.msg.value.code || "failed");
        else if (env.msg.case === "inventory") done(null);
      });
      // شبکه‌ای که جواب نمی‌دهد نباید دکمه را تا ابد در حالتِ «چند لحظه»
      // نگه دارد.
      const timer = setTimeout(() => done("network"), 8000);
    });
  }

  /**
   * پشتِ کارتی که به‌کار می‌بندم. رشته‌ی خالی = پیش‌فرض.
   *
   * REST و نه سوکت: مثلِ چهره و جای بازیکن، چیزی است که به حساب می‌چسبد و
   * نه به میز. خروجی: null = شد — و [[back]] همان لحظه عوض می‌شود تا میزِ بعدی
   * بی بارگذاریِ دوباره پشتِ تازه را بکشد.
   */
  async setBack(back: string): Promise<string | null> {
    const e = (await this.accountPost("/v1/account/back", { back })).error;
    if (!e) this.back.value = back;
    return e;
  }

  /** «میزهای من» از سرور. مهمان یا شبکه = همان که داریم (فهرستِ خالی اگر هیچ نداشتیم). */
  async loadMyTables(): Promise<void> {
    try {
      const j = await this.rest<{ tables?: MyTableRow[] }>("GET", "/v1/account/tables");
      this.myTables.value = j?.tables ?? [];
    } catch { if (this.myTables.value === null) this.myTables.value = []; }
  }

  /**
   * میزِ تازه (id خالی) یا ویرایشِ میزِ id. سرور داشتنِ شهر و فرش و پشت را می‌سنجد؛ کلاینت پیش از
   * این نداشته‌ها را می‌خرد. خروجی: میزِ ذخیره‌شده، یا کدِ خطا.
   */
  async saveMyTable(id: string, t: { name: string; table: string; back: string }): Promise<{ table?: MyTableRow; error?: string }> {
    try {
      const j = await this.rest<{ table: MyTableRow; tables: MyTableRow[] }>(id ? "PUT" : "POST",
        id ? `/v1/account/tables/${encodeURIComponent(id)}` : "/v1/account/tables", t);
      this.myTables.value = j.tables;
      return { table: j.table };
    } catch (e) { return { error: msgOf(e) }; }
  }

  async deleteMyTable(id: string): Promise<string | null> {
    try {
      const j = await this.rest<{ tables: MyTableRow[] }>("DELETE", `/v1/account/tables/${encodeURIComponent(id)}`);
      this.myTables.value = j.tables;
      return null;
    } catch (e) { return msgOf(e); }
  }

  /** پشتِ کارتِ به‌کاربسته از سرور — شاید روی گوشیِ دیگری عوض شده باشد. اگر در این فاصله همین
   *  صفحه عوضش کرد، جوابِ کهنه رویش نمی‌نشیند. */
  async refreshMe(): Promise<void> {
    const back = this.back.value;
    try {
      const me = await this.rest<{ back?: string }>("GET", "/v1/me");
      if (me && this.back.value === back) this.back.value = me.back ?? "";
    } catch { /* مهمان یا شبکه — همان که داریم */ }
  }

  /** کشور (از IP)، شهر و نمایشِ خودم از سرور. شکست یعنی همان که داریم. */
  async refreshPlace(): Promise<void> {
    if (!this.token) return;
    try {
      const me = await this.rest<{ country?: string; city?: string; show_place?: boolean }>("GET", "/v1/me");
      if (!me) return;
      this.country.value = me.country ?? "";
      this.city.value = me.city ?? "";
      this.showPlace.value = me.show_place !== false;
    } catch { /* شبکه — همان که داریم */ }
  }

  /** رمزِ تازه روی همین حساب. خروجی: null = شد. */
  async setPassword(password: string): Promise<string | null> {
    return (await this.accountPost("/v1/account/password", { password })).error;
  }

  /**
   * POST به مسیرِ حساب با توکنِ خودم.
   *
   * جدا از [[rest]] است چون آن یکی خطا را **پرتاب** می‌کند و بدنه‌اش را دور
   * می‌ریزد؛ این‌جا خودِ بدنه‌ی خطا داده است (`until` ِ سقفِ نام).
   */
  /**
   * بلیتِ صدای زنده‌ی میزِ فعلی — [[voice]]. `not_in_room` (این میز صدا ندارد:
   * صفِ سریع) و `voice_off` خطا نیستند، فقط «صدا نیست».
   */
  voiceTicket(): Promise<{ url?: string; token?: string; room?: string; can_speak?: boolean; error: string | null }> {
    return this.accountPost("/v1/voice/token", {});
  }

  /** میزبان: اجازه‌ی حرف‌زدن. خروجی: null = انجام شد، وگرنه not_host / not_here … */
  async voicePermit(body: { player_id?: string; all?: boolean; allow: boolean }): Promise<string | null> {
    const j = await this.accountPost("/v1/voice/permit", body);
    return j.error;
  }

  private async accountPost<T>(path: string, body: unknown): Promise<T & { error: string | null }> {
    if (!this.token) return { error: "unauthorized" } as T & { error: string };
    let res: Response;
    try {
      res = await fetch(this.base + path, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: "Bearer " + this.token },
        body: JSON.stringify(body),
      });
    } catch { return { error: "network" } as T & { error: string }; }
    if (res.status === 204) return { error: null } as T & { error: null };
    const j = (await res.json().catch(() => ({}))) as T & { error?: string };
    if (!res.ok) return { ...j, error: j.error ?? "http_" + res.status };
    return { ...j, error: null };
  }

  /**
   * عکسِ پروفایل. خروجی: نشانیِ عکس، یا خطا.
   *
   * سرور خودش رمزگشایی و از نو کدگذاری می‌کند (۲۵۶×۲۵۶ PNG)، پس این‌جا
   * کوچک‌کردن لازم نیست — فقط سقفِ حجم را زودتر می‌گیریم تا بازیکن با
   * اینترنتِ کند، شش مگابایت نفرستد و بعد «بزرگ است» بشنود.
   */
  async uploadAvatar(file: File): Promise<{ url?: string; error?: string }> {
    if (!this.token) return { error: "unauthorized" };
    if (file.size > 6 * 1024 * 1024) return { error: "too_large" };
    const body = new FormData();
    body.append("file", file);
    try {
      const res = await fetch(this.base + "/v1/account/avatar", {
        method: "POST",
        headers: { authorization: "Bearer " + this.token },
        body,
      });
      if (res.ok) {
        const j = (await res.json()) as { url?: string };
        return j.url ? { url: j.url } : { error: "store" };
      }
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      return { error: j.error || "http_" + res.status };
    } catch {
      return { error: "network" };
    }
  }

  /** عکسِ پروفایل را بردار — برگشت به چهره‌های خودِ بازی. */
  async clearAvatar(): Promise<string | null> {
    if (!this.token) return "unauthorized";
    try {
      const res = await fetch(this.base + "/v1/account/avatar", {
        method: "DELETE",
        headers: { authorization: "Bearer " + this.token },
      });
      return res.ok ? null : "http_" + res.status;
    } catch {
      return "network";
    }
  }

  /**
   * شهرِ خودم (اختیاری، فقط از IRAN_CITIES) و این‌که کشور و شهرم نشان داده شود.
   * کشور را نمی‌فرستیم؛ از IP است. خروجی: null = ثبت شد، وگرنه bad_place /
   * network … سرِ میزِ بعدی به دیگران می‌رسد (سرور عکسِ لحظه‌ی Hello را دارد)،
   * پس سوکت را از نو می‌بندیم تا همین حالا اثر کند.
   */
  async setPlace(city: string, show: boolean): Promise<string | null> {
    if (!this.token) return "network";
    try {
      const res = await fetch(this.base + "/v1/account/place", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: "Bearer " + this.token },
        body: JSON.stringify({ city, show }),
      });
      if (!res.ok) {
        try { return ((await res.json()) as { error?: string }).error ?? "http_" + res.status; } catch { return "http_" + res.status; }
      }
      this.city.value = city;
      this.showPlace.value = show;
      if (!this.table.value && !this.cbTable.value && !this.shTable.value && !this.hkTable.value && !this.board.value && !this.room.value) this.ws?.close();
      return null;
    } catch {
      return "network";
    }
  }

  private wokeHooked = false;

  /**
   * زبانه که از پس‌زمینه برمی‌گردد، عکسِ تازه بخواه.
   *
   * مرورگر زبانه‌ی پنهان را کند می‌کند و گاهی سوکت را نگه می‌دارد؛ بازیکن
   * برمی‌گردد و میزی می‌بیند که چند حرکت عقب است. یک درخواست، و صفحه سرِ جای
   * درست می‌نشیند — ارزان‌تر از وصلِ دوباره و بی‌آن‌که دستِ کسی دوباره پخش شود.
   */
  private hookWake(): void {
    if (this.wokeHooked || typeof document === "undefined") return;
    this.wokeHooked = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && this.state.value === "online") this.resync();
    });
  }

  private open(): void {
    this.hookWake();
    this.state.value = this.playerId.value ? "connecting" : "loggingIn";
    const ws = new WebSocket(this.base ? this.base.replace(/^http/, "ws") + "/ws" : wsUrl("/ws"));
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      this.sendNow(create(EnvelopeSchema, {
        msg: { case: "hello", value: { token: this.token, protocol: PROTOCOL, avatar: avatar.value, platform: "web", piece: menchPiece.value } },
      }));
    };
    ws.onmessage = (ev) => {
      // سوکتِ جایگزین‌شده هنوز چند فریم در راه دارد. بی این بررسی — که
      // onclose از قبل داشت و onmessage نداشت — وضعیتِ کهنه‌ی سوکتِ قبلی
      // روی وضعیتِ تازه می‌نشست و میز عقب می‌پرید.
      if (this.ws !== ws) return;
      let env: Envelope;
      try {
        env = fromBinary(EnvelopeSchema, new Uint8Array(ev.data as ArrayBuffer));
      } catch (e) {
        this.lastError.value = "پیام نامفهوم: " + msgOf(e);
        return;
      }
      this.apply(env);
    };
    ws.onclose = (ev) => {
      // سوکتِ کهنه (جایگزین‌شده) حرفی برای گفتن ندارد — بستنِ دیرهنگامش،
      // حتی با ۱۰۰۸، نباید اتصالِ تازه را «کنار گذاشته» جا بزند.
      if (this.ws !== ws) return;
      this.ws = null;
      // ۱۰۰۸ = سرور ما را کنار گذاشت چون همان حساب جای دیگری وصل شد.
      //
      // بی این بررسی، دو زبانه‌ی یک مرورگر بی‌پایان همدیگر را بیرون می‌انداختند و
      // هر کدام تکه‌ای از بازی را می‌دید — دقیقاً همان «انگار یک بازی نیست».
      if (ev.code === 1008) {
        this.want = false;
        this.state.value = "offline";
        this.kicked.value = true;
        this.lastError.value = "این حساب جای دیگری باز شد — این صفحه کنار گذاشته شد";
        return;
      }
      // خروج (signedOut) سوکت را عمداً می‌بندد؛ بستنِ دیرهنگامش نباید «آفلاین» جا بزند.
      if (!this.want) { if (this.state.value !== "signedOut") this.state.value = "offline"; return; }
      this.state.value = "reconnecting";
      this.schedule();
    };
    ws.onerror = () => {
      this.lastError.value = "اتصال قطع شد";
    };
  }

  /**
   * تنها جایی که وضعیت عوض می‌شود.
   *
   * عمومی است چون آزمون‌ها مستقیم پیام به آن می‌دهند: باگی که این‌جا بود فقط با
   * *دنباله‌ی* پیام‌ها دیده می‌شد، نه با یک پیامِ تنها.
   */
  apply(env: Envelope): void {
    if (this.stale(env)) return;
    switch (env.msg.case) {
      case "welcome":
        this.serverProtocol = env.msg.value.protocol;
        this.playerId.value = env.msg.value.playerId;
        this.playerName.value = env.msg.value.name;
        this.state.value = "online";
        this.lastError.value = "";
        // وصل شدیم، پس تعمیرات تمام شده — پرده باید همان لحظه برود.
        this.maintenance.value = "";
        this.backoff = 500;
        this.flush();
        // سرور سرِ همین اتصال کشور را از IP تازه کرد؛ نقشه‌ی خودم همان باشد.
        void this.refreshPlace();
        break;
      case "error":
        // «آرام‌تر» ِ سرور گذراست: پیامِ کوتاه و نه خطای ماندگارِ نوارِ اتصال — net/pace.ts.
        if (SLOW_CODES.has(env.msg.value.code)) { this.slowDown(); break; }
        // Hello با توکنِ کهنه (سرور نسلِ توکن را بالا برده — Backend/internal/auth): با همان
        // توکن دوباره زدن همان جواب را می‌گیرد. بی توکن، تلاشِ بعدی از نو وارد می‌شود — همان
        // کاری که اپ می‌کند.
        if (env.msg.value.code === "unauthorized" && this.state.value !== "online") this.token = "";
        this.lastError.value = errorText(env.msg.value.code, env.msg.value.message);
        if (env.msg.value.code === "maintenance") {
          this.maintenance.value = env.msg.value.message || MAINT_FA;
        }
        // اتاق رمز دارد: صفحه باید رمز بپرسد، نه این‌که فقط خطا نشان بدهد.
        if (env.msg.value.code === "room_password" && this.lastRoomCode) {
          this.needRoomPassword.value = this.lastRoomCode;
        }
        // میزی که تماشا می‌کردیم بسته شد: صفحه باید برگردد به فهرست، وگرنه
        // روی آخرین عکسِ میز یخ می‌زند و بازیکن فکر می‌کند بازی گیر کرده.
        //
        // مگر این‌که مسابقه تمام شده باشد: هر میزی که به نتیجه می‌رسد بلافاصله
        // بسته می‌شود، و پاک‌کردنِ صفحه در آن لحظه یعنی جدولِ نتایج یک چشم‌برهم‌زدن
        // بعد از «تمام شد» غیب می‌شود. آن‌جا تماشاچی خودش دکمه را می‌زند.
        if (env.msg.value.code === "watch_ended" && !this.tableOver()) this.clearWatch();
        // جام بسته شد (یا هرگز نبود، یا ثبت‌نام نکرده‌ایم): «بازیِ بعدی» دیگر
        // نباید به آن صف برگردد و هر بار همین خطا را بگیرد.
        if (CUP_GONE.has(env.msg.value.code)) this.cup.value = null;
        break;
      case "tableList":
        this.tables.value = env.msg.value.tables;
        break;
      case "chat":
        this.chat.value = [...this.chat.value, env.msg.value].slice(-CHAT_KEEP);
        break;
      case "chatHistory":
        // تاریخچه سرِ نشستن یا سرِ شروعِ تماشا می‌آید و جای هر چیزِ قبلی را
        // می‌گیرد: میزِ تازه یعنی چتِ تازه.
        this.chat.value = env.msg.value.messages.slice(-CHAT_KEEP);
        break;
      case "today":
        this.today.value = env.msg.value;
        break;
      case "wallet": {
        const w = env.msg.value;
        this.wallet.value = w;
        // عکسِ لحظه‌ای دلتا ندارد و نباید انیمیشن بسازد.
        if (w.deltaCoins !== 0n || w.deltaGems !== 0n) this.walletDelta.value = w;
        break;
      }
      case "subscription":
        this.subscription.value = env.msg.value;
        break;
      case "inventory": {
        const m = new Map<string, number>();
        for (const it of env.msg.value.items) m.set(it.item, it.qty);
        this.inventory.value = m;
        break;
      }
      case "roomState":
        this.room.value = env.msg.value;
        // اتاقی که هنوز شروع نشده یعنی از میزِ قبلی آمده‌ایم بیرون. میزِ کهنه را
        // پاک کن، وگرنه صفحه همان بازیِ قبلی را نشان می‌دهد و بازیکن فکر می‌کند
        // با حریفش در یک اتاق است در حالی که دو بازیِ متفاوت می‌بینند.
        if (!env.msg.value.started) {
          this.table.value = null;
          this.cbTable.value = null;
          this.board.value = null;
          this.mafia.value = null;
        }
        break;
      case "matchStatus":
        this.match.value = env.msg.value;
        break;
      case "notice":
        this.notices.value = [...this.notices.value, env.msg.value].slice(-50);
        this.lastNotice.value = env.msg.value;
        break;
      case "cupUpdate":
        this.cupUpdate.value = env.msg.value;
        break;
      // یک اتصال در هر لحظه سرِ **یک** میز است، پس آن یکی باید پاک شود. بی این،
      // میزِ حکمِ قبلی روی میزِ تازه‌ی چهاربرگ را می‌گرفت و تا تازه‌کردنِ صفحه
      // نمی‌رفت — «انگار اصلاً یک بازی نیست».
      case "tableEvent":
        if (env.msg.value.kind === "ace_draw") this.aceDraw.value = env.msg.value;
        break;
      case "hkEvent":
        if (env.msg.value.kind === "deal") this.hkDeal.value = env.msg.value;
        if (env.msg.value.kind === "ace_draw") this.hkAce.value = env.msg.value;
        break;
      case "cbEvent":
        if (env.msg.value.kind === "deal") this.cbDeal.value = env.msg.value;
        if (env.msg.value.kind === "ace_draw") this.cbAce.value = env.msg.value;
        break;
      case "shEvent":
        if (env.msg.value.kind === "deal") this.shDeal.value = env.msg.value;
        if (env.msg.value.kind === "ace_draw") this.shAce.value = env.msg.value;
        if (env.msg.value.kind === "widow") this.shWidow.value = env.msg.value;
        break;
      case "tableState":
        this.table.value = env.msg.value;
        this.cbTable.value = null;
        this.hkTable.value = null;
        this.shTable.value = null;
        this.board.value = null;
        this.mafia.value = null;
        this.match.value = null;
        break;
      case "cbState":
        this.cbTable.value = env.msg.value;
        this.table.value = null;
        this.hkTable.value = null;
        this.shTable.value = null;
        this.board.value = null;
        this.mafia.value = null;
        this.match.value = null;
        break;
      case "hkState":
        this.hkTable.value = env.msg.value;
        this.table.value = null;
        this.cbTable.value = null;
        this.shTable.value = null;
        this.board.value = null;
        this.mafia.value = null;
        this.match.value = null;
        break;
      case "shState":
        this.shTable.value = env.msg.value;
        this.table.value = null;
        this.cbTable.value = null;
        this.hkTable.value = null;
        this.board.value = null;
        this.mafia.value = null;
        this.match.value = null;
        break;
      case "mafiaState":
        this.mafia.value = env.msg.value;
        this.table.value = null;
        this.cbTable.value = null;
        this.hkTable.value = null;
        this.shTable.value = null;
        this.board.value = null;
        this.match.value = null;
        break;
      case "boardState":
        this.board.value = env.msg.value;
        this.table.value = null;
        this.cbTable.value = null;
        this.hkTable.value = null;
        this.shTable.value = null;
        this.match.value = null;
        break;
      default:
        break;
    }
    for (const fn of this.listeners) fn(env);
  }

  /**
   * این پیام از گذشته آمده؟
   *
   * پیام‌ها روی TCP به‌ترتیب **می‌رسند**، ولی همیشه به‌ترتیب **اعمال**
   * نمی‌شوند: سوکتِ کهنه‌ای که هنوز نمرده فریم می‌دهد، و کنترلرها گاهی پشتِ
   * صفِ انیمیشن وضعیت را دیر می‌نشانند. مهرِ `v` همان جایی است که این را
   * می‌گیرد — [[ver]] در سرور.
   *
   * پیامِ بی‌مهر (چت، کیف، فهرستِ میزها) از این دروازه رد می‌شود: ترتیبشان
   * مهم نیست و عقب‌بردنِ میز هم از آن‌ها برنمی‌آید.
   */
  private stale(env: Envelope): boolean {
    const m = env.msg;
    let v = 0n;
    let table: string | null = null;
    switch (m.case) {
      case "tableState": case "cbState": case "hkState": case "boardState": case "mafiaState":
        v = m.value.v; table = m.value.tableId; break;
      case "tableEvent": case "cbEvent": case "hkEvent": case "boardEvent": case "mafiaEvent":
        v = m.value.v; break;
      default:
        return false;
    }
    if (v === 0n) return false;          // سرورِ قدیمی‌تر؛ مثلِ قبل رفتار کن
    // میزِ عوض‌شده شمارنده‌ی خودش را دارد و از یک شروع می‌کند.
    if (table !== null && table !== this.vTable) {
      this.vTable = table;
      this.lastV = v;
      return false;
    }
    if (v <= this.lastV) return true;
    this.lastV = v;
    return false;
  }

  private schedule(): void {
    if (this.retry) clearTimeout(this.retry);
    // بی‌توکن (نامِ تازه) باید از ورود بگذرد؛ Helloِ بی‌توکن با «unauthorized» بسته می‌شد.
    this.retry = setTimeout(() => (this.token ? this.open() : void this.connect()), this.backoff);
    this.backoff = Math.min(this.backoff * 2, 8000);
  }

  private fail(text: string): void {
    this.lastError.value = text;
    this.state.value = "offline";
    if (this.want) {
      if (this.retry) clearTimeout(this.retry);
      this.retry = setTimeout(() => void this.connect(), 3000);
    }
  }

  private flush(): void {
    const pending = this.outbox;
    this.outbox = [];
    for (const env of pending) this.sendNow(env);
  }

  private sendNow(env: Envelope): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(toBinary(EnvelopeSchema, env));
  }

  /**
   * این صفحه را دوباره برگردان — بعد از این‌که جای دیگری کنارش گذاشته بود.
   *
   * فقط با کلیکِ آدم، هیچ‌وقت خودکار: آن زبانه هم همین کار را می‌کند و دو تا
   * با هم بی‌پایان همدیگر را بیرون می‌اندازند. کسی باید تصمیم بگیرد کدام
   * صفحه برنده است، و آن کس آدم است.
   */
  takeOver(): void {
    if (!this.kicked.value) return;
    this.lastError.value = "";
    void this.connect();
  }

  /** سرعت‌گیر کنار گذاشت: یک «کمی آرام‌تر» ِ گذرا، نه بیش از یکی در چند ثانیه. */
  private slowDown(): void {
    const now = Date.now();
    if (now - this.slowed.value > 2500) this.slowed.value = now;
  }

  /** بفرست، یا اگر هنوز وصل نیستیم نگه‌دار تا بعد از Welcome. */
  /** `false` یعنی نه رفت و نه در صفِ انتظار جا شد. */
  private send(msg: NonNullable<MessageInitShape<typeof EnvelopeSchema>["msg"]>): boolean {
    const env = create(EnvelopeSchema, { seq: ++this.seq, msg });
    if (this.state.value === "online") { this.sendNow(env); return true; }
    if (this.outbox.length >= 16) return false;
    this.outbox.push(env);
    void this.connect();
    return true;
  }

  /**
   * کنشی که سرِ میز می‌نشاند: بازیِ سریع، ربات، ساختن و پیوستنِ اتاق. بازی حساب نمی‌خواهد (کاربر، ۲۶ اکتبر
   * ۲۰۲۶: «باید بشود بی ورود بازی کرد و اتاق ساخت»)، پس مرورگرِ بی‌حساب مهمانش را همین دم می‌گیرد — نه با
   * بازکردنِ صفحه ([[connect]]). پیام در صندوقِ خروجی تا Welcome می‌مانَد. فقط میزِ ورودی‌دار حساب می‌خواهد
   * و آن را سرور می‌گوید (account_required).
   */
  private seat(msg: NonNullable<MessageInitShape<typeof EnvelopeSchema>["msg"]>): boolean {
    this.createGuest = true;
    return this.send(msg);
  }

  // ---- کنش‌ها ----
  /**
   * صفِ بازیِ سریع. stake سطحِ ورودیِ میزی است که می‌خواهی — صف به ازای هر
   * سطح جداست، پس هم‌بازی‌هایت همان عدد را گذاشته‌اند.
   */
  quickMatch(game = "hokm", stake = 0n): void {
    this.leaveCup();
    this.seat({ case: "quickMatch", value: { game, stake, solo: false } });
  }
  /** میزی با ربات‌ها، همین حالا — بی صف، بی ورودی. */
  soloMatch(game = "hokm"): void {
    this.leaveCup();
    this.seat({ case: "quickMatch", value: { game, stake: 0n, solo: true } });
  }
  /**
   * صفِ جامِ آرنا. ورودی همیشه صفر است (ثبت‌نام ورودی را گرفته) و بازی همان
   * بازیِ جام؛ سرور هر دو را خودش هم می‌گذارد، ولی بازی را می‌فرستیم تا سنجشِ
   * «این بازی صف دارد؟» ِ پیش از جام، بازیِ درست را ببیند.
   *
   * نتیجه‌ی میزِ قبلی پاک می‌شود تا پرده‌ی پایانِ میزِ بعد، عددِ کهنه نشان ندهد.
   */
  cupMatch(ref: CupRef): void {
    this.cup.value = ref;
    this.cupUpdate.value = null;
    this.send({ case: "quickMatch", value: { game: ref.game, stake: 0n, solo: false, cup: ref.id } });
  }
  /**
   * جامی که «بازیِ بعدی» به آن برمی‌گردد — اگر هنوز باز است. پایانِ جام که
   * گذشت، خودش پاک می‌شود: صفِ جامِ بسته فقط `cup_closed` می‌دهد.
   */
  liveCup(now = Date.now()): CupRef | null {
    const c = this.cup.value;
    if (c && c.endsAtMs <= now) { this.cup.value = null; return null; }
    return c;
  }
  /** میزی که حالا می‌گیریم مالِ جام نیست — [[cup]]. */
  private leaveCup(): void {
    this.cup.value = null;
    this.cupUpdate.value = null;
  }
  cancelMatch(): void { this.send({ case: "cancelMatch", value: {} }); }
  /**
   * ساختِ اتاق. stake ورودیِ سکه‌ایِ هر بازیکن است و فقط سطح‌های مجاز
   * (۱۰۰/۵۰۰/۲۰۰۰) معنا دارند — هر عددِ دیگری را سرور به میزِ رایگان تبدیل
   * می‌کند، پس فهرستِ UI باید با economy.StakeLevels یکی بماند.
   */
  /** rules: قانون‌های میزبان برای همین بازی (سرور می‌سنجد؛ مارپله: dice ۰/۱/۲ و magic ۰/۱). */
  createRoom(game = "hokm", seats = 4, carpet = "eyvan", stake = 0n, isPublic = false, password = "", rules: Record<string, number> = {}): void {
    if (!this.roomPace.take("create:" + game)) return; // دو‌بار‌زدنِ «بساز»: یک اتاق
    this.leaveCup();
    this.seat({ case: "createRoom", value: { game, seats, carpet, stake, isPublic, password, rules } });
  }

  /** میزِ اتاق عمومی/خصوصی — فقط میزبان، پیش از شروع. */
  setPublic(isPublic: boolean): void {
    if (!this.hostPace.take()) { this.slowDown(); return; }
    this.send({ case: "setPublic", value: { isPublic } });
  }

  /** پیامِ متنی روی چتِ میز. سرور خودش می‌بُرد و پاک می‌کند. */
  /**
   * پیامِ چت. `false` یعنی نرفت و **جای دیگری هم نگه داشته نشد**.
   *
   * پاسخ برمی‌گرداند چون جعبه‌ی متن روی همین تصمیم می‌گیرد که خودش را پاک
   * کند یا نه: پیش از این بی‌قیدوشرط پاک می‌شد و پیامی که سرِ اتصالِ قطع
   * نوشته شده بود، بی هیچ نشانی از صفحه می‌رفت.
   */
  sendChat(text: string): boolean {
    const t = text.trim();
    if (!t) return false;
    // همان متن دوباره، یا تندتر از سهمیه: نمی‌رود و جعبه‌ی متن پُر می‌مانَد — net/pace.ts.
    if (!this.chatPace.take("t:" + t)) { this.slowDown(); return false; }
    return this.send({ case: "sendChat", value: { text: t } });
  }

  /** «امروز چه خبر است» — بازیِ روز و مأموریت‌ها. پاسخ در `today` می‌نشیند. */
  askToday(): void { this.send({ case: "askToday", value: {} }); }

  /**
   * جایزه‌ی یک مأموریت. پاسخ همیشه یک `TodayState` ِ تازه است — چه گرفته
   * شود چه نه — پس صفحه خودش را از سرور می‌خوانَد و حدس نمی‌زند.
   */
  claimMission(missionId: string): void {
    this.send({ case: "claimMission", value: { missionId } });
  }

  /**
   * عبارتِ آماده — کلید می‌رود و نه متن، تا هر کلاینت متنِ خودش را بسازد. `false` یعنی سرعت‌گیر
   * کنار گذاشت: همان عبارت دوباره، یا بیش از سهمیه‌ی این چند ثانیه (دکمه‌ها با [[cannedUntil]] کم‌رنگ‌اند).
   */
  sendCanned(canned: string): boolean {
    if (this.cannedUntil(canned)) { this.slowDown(); return false; }
    this.chatPace.take("c:" + canned);
    this.cannedPace.take();
    return this.send({ case: "sendChat", value: { canned } });
  }

  /** تا کِی (Date.now) این عبارتِ آماده خاموش است؛ ۰ = آزاد. */
  cannedUntil(canned: string): number {
    return Math.max(this.chatPace.until("c:" + canned), this.cannedPace.until());
  }

  /** بستن/بازکردنِ چتِ میز — فقط میزبان، در اتاق یا وسطِ بازی. */
  setChatOpen(open: boolean): void {
    if (!this.hostPace.take()) { this.slowDown(); return; }
    this.send({ case: "setChatOpen", value: { open } });
  }

  /**
   * عکسِ تازه‌ی میز را بخواه.
   *
   * وقتی لازم می‌شود: زبانه از پس‌زمینه برمی‌گردد (مرورگر سوکت را کند کرده
   * بوده)، یا چیزی روی صفحه با حس‌وحالِ بازی نمی‌خوانَد. پاسخ از همان سوکت و
   * با `v`ِ خودش می‌آید، پس چند بار خواستنش هم بی‌خطر است.
   */
  resync(): void { this.send({ case: "resync", value: {} }); }

  /** فهرستِ میزهای عمومی. game خالی = همه. */
  listTables(game = ""): void { this.send({ case: "listTables", value: { game } }); }

  /**
   * تماشای یک میز. صندلی نمی‌گیری: وضعیت‌ها با `watching` می‌آیند و دستِ
   * هیچ‌کس در آن‌ها نیست. میزهای کهنه پاک می‌شوند تا صفحه‌ی میزِ قبلی یک فریم
   * روی میزِ تازه نماند.
   */
  watchTable(tableId: string): void {
    if (!this.roomPace.take("watch:" + tableId)) return; // دو‌بار‌زدنِ همان میز
    this.table.value = null;
    this.cbTable.value = null;
    this.hkTable.value = null;
    this.shTable.value = null;
    this.board.value = null;
    this.mafia.value = null;
    this.aceDraw.value = null;
    this.hkDeal.value = null;
    this.hkAce.value = null;
    this.cbAce.value = null;
    this.shAce.value = null;
    this.shDeal.value = null;
    this.shWidow.value = null;
    this.cbDeal.value = null;
    this.chat.value = [];
    this.watching.value = tableId;
    this.send({ case: "watchTable", value: { tableId } });
  }

  /**
   * «برگرد» سرِ پایانِ بازی — یک دکمه، دو معنا.
   *
   * بازیکن میزش را ترک می‌کند و تماشاچی تماشا را تمام می‌کند. بی این، دکمه‌ی
   * پایانِ مسابقه برای تماشاچی leaveRoom می‌فرستاد: بی‌اثر، و صفحه روی میزِ
   * تمام‌شده گیر می‌کرد.
   */
  exitTable(): void {
    if (this.watching.value) this.stopWatch();
    else this.leaveRoom();
  }

  /** پایانِ تماشا — برگشت به فهرست. */
  stopWatch(): void {
    this.send({ case: "stopWatch", value: {} });
    this.clearWatch();
  }

  /** میزی که روی صفحه است به نتیجه رسیده؟ */
  private tableOver(): boolean {
    return !!(this.table.value?.matchOver || this.cbTable.value?.matchOver || this.shTable.value?.matchOver || this.hkTable.value?.matchOver || this.board.value?.over || this.mafia.value?.over);
  }

  private clearWatch(): void {
    if (!this.watching.value) return;
    this.watching.value = "";
    this.chat.value = [];
    this.table.value = null;
    this.cbTable.value = null;
    this.hkTable.value = null;
    this.shTable.value = null;
    this.board.value = null;
    this.mafia.value = null;
  }
  /** هفت روزِ آزمایشی. رسید نمی‌خواهد، پس پیش از پولکی هم کار می‌کند. */
  startTrial(): void { this.send({ case: "startTrial", value: {} }); }

  /** فرشِ اتاق — فقط میزبان؛ سرور به همه پخش می‌کند. */
  setCarpet(carpet: string): void {
    if (!this.hostPace.take()) { this.slowDown(); return; }
    this.send({ case: "setCarpet", value: { carpet } });
  }
  /**
   * ربات روی صندلیِ خالیِ اتاقِ تخته‌ای (bot = true) یا برداشتنش — فقط میزبان، پیش از شروع (کاربر، ۲۶ اکتبر ۲۰۲۶:
   * «اگر کسی نیست، باید بتوانم انتخاب کنم چند ربات بیاید»). سرور به همه پخش می‌کند — match.Rooms.SetBot.
   */
  setRoomBot(seat: number, bot: boolean): void {
    if (!this.hostPace.take()) { this.slowDown(); return; }
    this.send({ case: "setRoomBot", value: { seat, bot } });
  }
  /** رمز فقط وقتی لازم است که سرور `room_password` داده باشد. */
  joinRoom(code: string, password = ""): void {
    if (!this.roomPace.take("join:" + code + "|" + password)) return; // دو‌بار‌زدنِ «بپیوند»
    this.leaveCup();
    this.lastRoomCode = code;
    this.seat({ case: "joinRoom", value: { code, password } });
  }
  leaveRoom(): void {
    this.send({ case: "leaveRoom", value: {} });
    // سرور هم با هر «جای دیگری رفتن» تماشا را تمام می‌کند؛ این‌جا فقط صفحه را
    // با آن هم‌گام می‌کنیم.
    this.watching.value = "";
    this.chat.value = [];
    this.room.value = null;
    this.table.value = null;
    this.cbTable.value = null;
    this.hkTable.value = null;
    this.shTable.value = null;
    this.board.value = null;
    this.mafia.value = null;
    this.aceDraw.value = null;
    this.hkDeal.value = null;
    this.hkAce.value = null;
    this.cbAce.value = null;
    this.shAce.value = null;
    this.shDeal.value = null;
    this.shWidow.value = null;
    this.cbDeal.value = null;
  }
  /** دو‌بار‌زدنِ «شروع» یک شروع است — دومی فقط «در اتاق نیستی» می‌گرفت. */
  startRoom(): void {
    if (!this.roomPace.take("start")) return;
    this.send({ case: "startRoom", value: {} });
  }
  callTrump(suit: Suit): void { this.send({ case: "callTrump", value: { suit } }); }
  playCard(card: Card): void { this.send({ case: "playCard", value: { card } }); }
  /** «ادامه» ِ پنلِ پایانِ دست/دور — [[BreakWatch]]. */
  handReady(): void { this.send({ case: "handReady", value: {} }); }
  shBid(amount: number): void { this.send({ case: "shBid", value: { pass: false, amount } }); }
  shPass(): void { this.send({ case: "shBid", value: { pass: true, amount: 0 } }); }
  shDiscard(cards: Card[]): void { this.send({ case: "shDiscard", value: { cards } }); }
  shTrump(suit: Suit): void { this.send({ case: "shTrump", value: { suit } }); }
  shPlay(card: Card): void { this.send({ case: "shPlay", value: { card } }); }
  cbPlay(card: Card): void { this.send({ case: "cbPlay", value: { card } }); }
  cbPickCapture(option: number): void { this.send({ case: "cbPickCapture", value: { option } }); }
  hkPlay(card: HkCard): void { this.send({ case: "hkPlay", value: { card } }); }
  hkDraw(): void { this.send({ case: "hkDraw", value: {} }); }
  hkPickSuit(suit: Suit): void { this.send({ case: "hkPickSuit", value: { suit } }); }
  hkPickTarget(seat: number): void { this.send({ case: "hkPickTarget", value: { seat } }); }
  hkCallWarn(): void { this.send({ case: "hkCallWarn", value: {} }); }
  hkCatch(): void { this.send({ case: "hkCatch", value: {} }); }
  hkPickOffer(index: number): void { this.send({ case: "hkPickOffer", value: { index } }); }
  /** حرکتِ میزِ تخته‌ای: kind ۱ تاس، ۲ حرکت، ۳ برداشتن — a/b/c مالِ همان بازی. */
  boardMove(kind: number, a = 0, b = 0, c = 0): void { this.send({ case: "boardMove", value: { kind, a, b, c } }); }
  /** کارِ مافیا — ۱ جواب، ۲ هیچ، ۳ جمله، ۴ تمام، ۵ پیشنهاد (MafiaAction در table.proto). */
  mafiaAction(a: MessageInitShape<typeof MafiaActionSchema>): void { this.send({ case: "mafiaAction", value: a }); }

  /**
   * آدمکِ منچ عوض شد (۰ پسر، ۱ دختر): وصل، همین حالا به سرور؛ وگرنه Helloِ بعدی می‌بردش. سرور آن را سرِ
   * پیوستن به اتاق برمی‌دارد، پس میزی که از قبل نشسته‌ای همان آدمکِ قبلی را نگه می‌دارد.
   */
  setMenchPiece(p: number): void {
    saveMenchPiece(p);
    if (this.state.value === "online") this.send({ case: "setPiece", value: { piece: menchPiece.value } });
  }

  /**
   * چهره عوض شد: سرور چهره را از Hello می‌خوانَد، پس یک بارِ دیگر وصل می‌شویم.
   *
   * نام این‌جا نیست: آن یکی توکنِ تازه می‌خواهد و راهِ خودش را دارد
   * ([[setName]]).
   */
  reconnectForIdentity(): void {
    this.ws?.close();
  }

  /** قطعِ ساختگی — برای آزمودنِ مسیرِ بازگشت. */
  drop(): void { this.ws?.close(); }

  /** خداحافظی؛ دیگر تلاشِ بازگشتی نمی‌کند. */
  close(): void {
    this.want = false;
    if (this.retry) clearTimeout(this.retry);
    this.ws?.close();
    this.state.value = "offline";
  }
}

/** کلیدِ یکتاییِ یک تلاشِ خرید — تکرارِ شبکه نباید دو بار بخرد. */
function newRef(): string {
  try { return crypto.randomUUID(); } catch { return "b" + Date.now() + Math.random().toString(36).slice(2); }
}

/** نامِ محلی برای ساختنِ مهمان — نسلِ قبل، و «بی حساب بنشین» ([[seatAsGuest]]). */
const NAME_KEY = "bazivan.name";

/**
 * نامِ موقتِ مهمانِ تازه — «مهمان ۴۸۲۷»، همان قالبِ اپ (ProgressSystem.EnsureName) و سرور (store.tempName). بی آن
 * مهمانِ وب که اسمی نزده بود سرِ میز بی‌نام بود و هم‌بازی‌ها «حریف» می‌دیدند، و لینکِ اتاقش نامِ میزبان نداشت. عدد
 * از شناسه‌ی دستگاه می‌آید، پس هر تلاشِ دوباره همان است. ثبت‌نام جایش را با نامِ کاربری می‌گیرد و عوض‌کردنش سقفِ
 * هفت‌روزه را شروع نمی‌کند.
 */
export function tempGuestName(device: string): string {
  let h = 0;
  for (const ch of device) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return "مهمان " + String(1000 + (h % 9000)).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]!);
}

function savedName(): string {
  try { return localStorage.getItem(NAME_KEY) ?? ""; } catch { return ""; }
}

function msgOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** کدِ خطای پاسخِ JSON ِ سرور، بی خراب‌کردنِ بدنه (روی clone). */
async function errorOf(res: Response): Promise<string> {
  try { return ((await res.clone().json()) as { error?: string }).error ?? ""; } catch { return ""; }
}

/**
 * یک جمله، همه‌جا: نوارِ سایت، پرده‌ی /play، خطای سوکت، و صندوق.
 *
 * در یک جا نوشته می‌شود تا وقتی عوض شد، نیمی از صفحه‌ها جمله‌ی قبلی را
 * نگویند. برادرِ سمتِ سایتش MAINT_FA در site/live.js است — آن یکی
 * باندل نمی‌شود و ناچار تکرار است.
 */
export const MAINT_FA = "سرویس در حال به‌روزرسانی است";

/** خطاهای سرعت‌گیرِ سرور (Backend/internal/ws/pace.go) — گذرا، نه lastError. */
const SLOW_CODES = new Set(["chat_too_fast", "too_many"]);

/** کدِ خطای سرور → متنِ فارسی. متنِ انگلیسیِ سرور فقط برای لاگ است. */
export function errorText(code: string, fallback = ""): string {
  switch (code) {
    case "unauthorized": return "ورود معتبر نیست";
    case "banned": return "حسابت محدود شده است" + (fallback ? " — " + fallback : "");
    case "maintenance": return MAINT_FA + "؛ کمی بعد بیا";
    case "muted": return "چت تو تا مدتی بسته است";
    case "not_your_turn": return "نوبت تو نیست";
    case "illegal_card": return "این کارت را نمی‌شود بازی کرد";
    case "room_full": return "آن اتاق پر است";
    // ربات‌نشاندنِ میزبان (match.Rooms.SetBot): همان دم دوستی آن صندلی را گرفت، یا بازی‌ای که جای خالی‌اش خودش ربات است
    case "seat_not_open": return "این صندلی همین حالا پر شد";
    case "no_room_bots": return "در این بازی جای خالی خودش ربات می‌گیرد";
    // میزبان بلاکت کرده (ws/safety.go) — عمداً همین‌قدر گنگ: «بلاکت کرده» را نمی‌گوییم.
    case "room_unavailable": return "این اتاق در دسترست نیست";
    case "room_not_found": return "اتاقی با این کد نیست";
    case "room_password": return "رمز اتاق درست نیست";
    case "room_locked_out": return "چند رمز غلط پشت سر هم — از میزبان کد و رمز تازه بگیر";
    case "not_host": return "فقط میزبان می‌تواند شروع کند";
    case "bad_protocol": return "نسخه‌ی بازی قدیمی است — صفحه را تازه کن";
    case "unsupported_game": return "این بازی هنوز آنلاین نیست";
    case "rooms_only": return "این بازی آنلاین فقط با دوستان است. اتاق بساز";
    case "table_error": return "میز به خطا خورد — دوباره بنشین";
    case "already_queued": return "از قبل در صف هستی";
    case "insufficient_coins": return "سکه کافی نیست";
    // میزِ ورودی‌دار حسابِ فعال می‌خواهد؛ بازی و اتاقِ رایگان نه (Backend/internal/ws Hub.activeAccount)
    case "account_required": return "میز ورودی‌دار حساب می‌خواهد — وارد شو، یا رایگان بازی کن";
    case "already_owned": return "این را از قبل داری";
    case "unknown_item": return "این قلم برای فروش نیست";
    case "trial_used": return "دوره‌ی آزمایشی‌ات را استفاده کرده‌ای";
    case "table_not_found": return "این میز دیگر برای تماشا باز نیست";
    case "watch_ended": return "میز بسته شد";
    case "busy_at_table": return "اول از میز خودت بیرون بیا";
    case "table_full_of_watchers": return "ظرفیت تماشاچی این میز پر است";
    case "chat_closed": return "چت این میز بسته است";
    case "chat_too_fast": return "کمی آرام‌تر";
    case "too_many": return "کمی آرام‌تر";
    case "role_claim": return "نام نقش پوشانده شد. گفتن نقش (اطلاق) در مافیا ممنوع است.";
    case "bad_chat": return "این پیام فرستاده نشد";
    case "cup_not_found": return "این جام دیگر نیست";
    case "cup_closed": return "صف جام الان باز نیست";
    case "cup_not_joined": return "اول در جام ثبت‌نام کن";
    default: return fallback || code;
  }
}

/**
 * جامی که در صفش می‌ایستیم — آن‌قدر که «بازیِ بعدی» و صفحه‌ی صف لازم دارند.
 * endsAtMs: پایانِ جام؛ بعد از آن صفِ جام بسته است.
 */
export type CupRef = { id: string; game: string; title: string; endsAtMs: number };

/** خطاهای صفِ جام که یعنی «این جام دیگر صفی برای تو ندارد». */
const CUP_GONE = new Set(["cup_not_found", "cup_closed", "cup_not_joined"]);

export const client = new Client();

/** جوابِ ورودِ مهمان. `username` خالی = مهمان؛ ثبت‌نام و ورود در لندینگ‌اند (site/auth.js). */
type AuthResp = {
  token: string; player_id: string; name: string; username?: string; email?: string; verified?: boolean;
  country?: string; city?: string; show_place?: boolean;
  /** پشتِ کارتِ به‌کاربسته؛ سرورِ کهنه نمی‌فرستد = پیش‌فرض. */
  back?: string;
};
